/**
 * Small dual-encoded badges used on every value: the reading source (JSON,
 * SIM, LIVE, SNAPSHOT), its freshness, and the status pill (colour, icon and
 * text). Colours alone never carry meaning here.
 */
import { CircleCheck, CircleDot, CircleMinus, CircleX } from 'lucide-react';
import { Chip } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import type { ReadingSource, StatusColour } from '../../types';
import { fmtAge } from '../lib/format';
import { colourLabel, freshnessTone, SOURCE_LABEL, toneOf } from '../lib/values';

export function SourceBadge({ source, className, pulse }: { source: ReadingSource; className?: string; pulse?: boolean }) {
  return (
    <span
      title={`Source: ${SOURCE_LABEL[source]}`}
      className={cn(
        'inline-flex items-center gap-1 rounded-control border px-1 py-px font-mono text-[9px] font-semibold uppercase tracking-wider',
        source === 'live' && 'border-live/40 text-live',
        source === 'sim' && 'border-sim/40 text-sim',
        (source === 'json' || source === 'snapshot') && 'border-hairline-strong text-ink-3',
        className,
      )}
    >
      {pulse && source === 'live' && <span className="h-1 w-1 animate-pulse rounded-full bg-live" />}
      {SOURCE_LABEL[source]}
    </span>
  );
}

export function Freshness({ ageMs, freshMs = 60_000, className }: { ageMs: number; freshMs?: number; className?: string }) {
  const tone = freshnessTone(ageMs, freshMs);
  return (
    <span
      title={`Updated ${fmtAge(ageMs)} ago`}
      className={cn(
        'inline-flex items-center rounded-control px-1 py-px font-mono text-[9px] tabular',
        tone === 'nominal' && 'text-ink-3',
        tone === 'caution' && 'text-caution',
        tone === 'warning' && 'text-warning',
        className,
      )}
    >
      {fmtAge(ageMs)}
    </span>
  );
}

export function StatusIcon({ colour, className }: { colour: StatusColour | null | undefined; className?: string }) {
  const cls = cn('h-3.5 w-3.5 shrink-0', className);
  if (colour === 'green') return <CircleCheck className={cn(cls, 'text-nominal')} aria-label="Operational" />;
  if (colour === 'yellow') return <CircleMinus className={cn(cls, 'text-caution')} aria-label="Warning" />;
  if (colour === 'red') return <CircleX className={cn(cls, 'text-warning')} aria-label="Critical" />;
  return <CircleDot className={cn(cls, 'text-ink-3')} aria-label="Unknown" />;
}

/** Colour plus icon plus text: the status is readable without colour. */
export function StatusPill({ colour, label, className, pulse }: { colour: StatusColour | null | undefined; label?: string; className?: string; pulse?: boolean }) {
  return (
    <Chip tone={toneOf(colour)} className={className} pulse={pulse}>
      <StatusIcon colour={colour} className="h-3 w-3" />
      {label ?? colourLabel(colour)}
    </Chip>
  );
}

/** Count bubbles for red and yellow descendants (the previous data-issueCount and data-warningCount). */
export function IssueBadges({ red, yellow, className }: { red: number; yellow: number; className?: string }) {
  if (red === 0 && yellow === 0) return null;
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {red > 0 && (
        <span title={`${red} critical`} className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-warning/20 px-1 font-mono text-[10px] font-semibold text-warning">
          {red}
        </span>
      )}
      {yellow > 0 && (
        <span title={`${yellow} warning`} className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-caution/20 px-1 font-mono text-[10px] font-semibold text-caution">
          {yellow}
        </span>
      )}
    </span>
  );
}
